import { AuthenticationResult, EventType } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppContext } from "src/contexts/AppContext";
import { LoadingContext } from "src/contexts/LoadingContext";
import { useSnackBar } from "src/contexts/SnackbarContext";
import { useQuery } from "src/core/Hooks";
import { CoreInterfaces, DTOs } from "src/core/Models";
import i18n from "src/i18n";
import {
  fetchData,
  fetchEngagementConfigurationData,
  fetchPdfFile,
  formatDate,
  generateEngagementDescriptionWordDocument,
  generateEngagementDocument,
  generatePdfDocument,
  getEngagementTotals,
  isDigitallySigningChecked,
  isEngagementInFinalVersion,
  loadFile,
  sendFile,
  setDocumentTitle,
  updateStateOnEngagementConfigurationRead,
} from "src/utils";
import {
  doCancelSigningProcess,
  fetchDownloadSignedDocument,
  fetchEngagementDocuments,
  fetchEngagementLetterSignnes,
  fetchSigningStatus,
  handleFileUpload,
  deleteDocument,
} from "src/utils/documents-manager";
import {
  deleteSPEngagementFolder,
  deriveNamesFromEmail,
  executeFinalizeEngagementDocumentFlow,
  extractSigningIdFromUrl,
  isDigitallySigned,
  isEmailContainingForbiddenCharactersForDigitalSigningUsers,
  isEmailMissingForDigitalSigningUsers,
  isSigningObsolete,
  openSharepointURLInMsWordClient,
  removeOldMetadataDocuments,
  resolveDigitalSigningContacts,
  resolveGenerateDocumentsPageConfiguration,
  resolveSPFolder,
  sendFileToSharepoint,
} from "src/utils/generate-documents";
import {
  handleConfigurationPersistence,
  saveCurrentConfiguration,
} from "src/utils/handle-configuration-utils";
import { applyUpdatesOnQuestionChange } from "src/utils/processors/Questions";
import * as Constants from "src/core/Constants";
import { loginRequest, sharepointAccessRequestConfig } from "src/msalConfig";
import { environment } from "src/core/Configurations/Environment.config";

export let accessToken: string;

export const setAccessToken = (val: string) => {
  accessToken = val;
};

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
};

const INITIAL_PAGE_STATE: CoreInterfaces.PageState = {
  title: "Pages.GenerateDocuments.Title",
};

export function useGenerateDocuments() {
  const { globalState, dispatch, businessUnit } = useContext(AppContext);
  const { updateApplicationLoadingState, isLoading: isPageLoaderVisible } =
    useContext(LoadingContext);
  const { t } = useTranslation();
  const queryParamsMap = useQuery();
  const { instance } = useMsal();
  const { showSnackBar } = useSnackBar();

  const [
    isEngagementLetterRegenerateConfirmModalVisible,
    setIsEngagementLetterRegenerateConfirmModalVisible,
  ] = useState(false);
  const [engagementDocuments, setEngagementDocuments] = useState<
    Array<CoreInterfaces.EngagementDocument>
  >([]);

  const [engagementLetterSignees, setEngagementLetterSignees] = useState<
    Array<CoreInterfaces.EngagementLetterSignee>
  >([]);

  const [envelopeIds, setEnvelopeIds] = useState<Record<number, string>>({});

  const businessOpportunityId = queryParamsMap.get(
    Constants.QueryParams.businessOpportunityId
  );

  const clientId = queryParamsMap.get(Constants.QueryParams.clientId);
  const pageState = INITIAL_PAGE_STATE;
  const [isVisibleSetFinalDocumentDialog, setIsVisibleSetFinalDocumentDialog] =
    useState(false);
  const [
    isVisibleSetManuallySignedDialog,
    setIsVisibleSetManuallySignedDialog,
  ] = useState(false);

  const [isVisibleCancelSigningDialog, setIsVisibleCancelSigningDialog] =
    useState(false);
  const [isVisibleChangeEmailDialog, setIsVisibleChangeEmailDialog] =
    useState(false);
  const [isVisibleNotifyDialog, setIsVisibleNotifyDialog] = useState(false);
  const [selectedEngagementLetterSignee, setSelectedEngagementLetterSignee] =
    useState(null);

  const [language, setLanguage] = useState(
    (localStorage.getItem(
      Constants.i18nDocumentStorageKey
    ) as Constants.Languages) || Constants.Languages.SE
  );

  useEffect(() => {
    if (
      isEngagementInFinalVersion(globalState) &&
      globalState.currentConfiguration.engagementDocumentsLanguage
    ) {
      setLanguage(globalState.currentConfiguration.engagementDocumentsLanguage);
      localStorage.setItem(
        Constants.i18nDocumentStorageKey,
        globalState.currentConfiguration.engagementDocumentsLanguage
      );
    }
  }, [isEngagementInFinalVersion(globalState)]);

  useEffect(() => {
    setDocumentTitle(t(pageState.title));
  }, []);

  useEffect(() => {
    const callbackId = instance.addEventCallback(async (event) => {
      if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
        (event.payload as AuthenticationResult).account
      ) {
        setAccessToken((event.payload as AuthenticationResult).accessToken);
      }
    });
    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance]);

  useEffect(() => {
    if (globalState.currentConfiguration.id) {
      if (accessToken) {
        const documentsFlow = localStorage.getItem(
          Constants.LOCAL_STORAGE_DOCUMENT_FLOW_KEY
        );
        if (!!documentsFlow) {
          if (
            documentsFlow ===
            Constants.GenerateDocumentsDocumentFlow.SendFilesToSharepoint
          ) {
            handleGenerateDocuments(accessToken);
          } else if (
            documentsFlow ===
            Constants.GenerateDocumentsDocumentFlow.SetFinalVersion
          ) {
            createFinalDocument(accessToken);
          }
          setAccessToken(undefined);
          localStorage.removeItem(Constants.LOCAL_STORAGE_FORCE_REDIRECT_KEY);
        } else {
          const isSendForDigitalSigningFlow = !!localStorage.getItem(
            Constants.LOCAL_STORAGE_SEND_FOR_DIGITAL_SIGNING_FLOW_KEY
          );
          if (
            documentPageConfig.isFinalVersion &&
            !digitallySigned &&
            isSendForDigitalSigningFlow
          ) {
            if (!isPageLoaderVisible) {
              updateApplicationLoadingState(true);
            }
            localStorage.removeItem(
              Constants.LOCAL_STORAGE_SEND_FOR_DIGITAL_SIGNING_FLOW_KEY
            );
            sendForDigitalSigningDocument();
          }
        }
      }
      fetchAndPopulateEngagementDocuments();
    }
  }, [globalState.currentConfiguration.id]);

  useEffect(() => {
    if (
      (digitallySigned || signingObsolete) &&
      !!globalState.currentConfiguration.digitalSigningDetails?.Version &&
      globalState.currentConfiguration.digitalSigningDetails?.CaseId &&
      globalState.currentConfiguration.digitalSigningDetails?.DocumentId
    ) {
      fetchAndPopulateEngagementLetterSignees();
    }
  }, [
    globalState.currentConfiguration.digitalSigningDetails?.CaseId,
    globalState.currentConfiguration.digitalSigningDetails?.DocumentId,
  ]);

  useEffect(() => {
    if (
      !!globalState.currentConfiguration.signingStatus &&
      globalState.currentConfiguration.signingStatus ===
        Constants.SigningStatus.DigitallySignedDeclined
    ) {
      signingStatusChange();
    }
  }, [globalState.currentConfiguration.signingStatus]);

  const configurationNo = globalState.currentConfiguration.version
    ? globalState.currentConfiguration.version
    : "1";
  const configurationName = t("General.ConfigurationVersionNumber", {
    configurationNo,
  });
  const description = globalState.currentConfiguration.description
    ? globalState.currentConfiguration.description
    : "";

  const signingStatus = globalState.currentConfiguration.signingStatus;
  const digitallySigned = isDigitallySigned(signingStatus);
  const signingObsolete = isSigningObsolete(signingStatus);

  const genericService = globalState.services.find(
    (serviceDTO) =>
      serviceDTO.data.code === Constants.ServiceCode.GeneralInformation
  );

  const createdBy = t("General.CreatedBy", {
    name: globalState.currentConfiguration.createdBy,
    datetime: new Date(
      globalState.currentConfiguration.createdDate
    ).toLocaleDateString([], DATE_FORMAT_OPTIONS),
  });

  const documentPageConfig = resolveGenerateDocumentsPageConfiguration(
    globalState,
    businessUnit
  );

  const engagementDescriptionDocumentArray = [];
  if (documentPageConfig.isFinalVersion) {
    const engagementDocument = engagementDocuments.find(
      (engagementDocument) =>
        engagementDocument.Type ===
        Constants.EngagementDocumentType.EngagementDescription
    );
    if (engagementDocument) {
      engagementDescriptionDocumentArray.push({
        ...engagementDocument,
        OriginalFileName: t("General.EngagementDescription"),
      });
    }
  } else {
    engagementDescriptionDocumentArray.push({
      Id: "EngagementDescriptionDocument",
      OriginalFileName: t("General.GenerateEngagementDescription"),
      DocumentType: Constants.EngagementDocumentType.EngagementDescription,
    });
  }

  const isEmailMissingDigitalSigningUsers =
    isEmailMissingForDigitalSigningUsers(globalState);

  const isEmailContainingForbiddenCharactersDigitalSigningUsers =
    isEmailContainingForbiddenCharactersForDigitalSigningUsers(globalState);

  function fetchAndPopulateEngagementDocuments(handleLoading = false): void {
    if (handleLoading) {
      updateApplicationLoadingState(true);
    }
    fetchEngagementDocuments(globalState.currentConfiguration.id)
      .then((engagementDocuments: Array<CoreInterfaces.EngagementDocument>) => {
        setEngagementDocuments(engagementDocuments);
        if (documentPageConfig.isFinalVersion && !digitallySigned) {
          const engagementDescriptionDocument = engagementDocuments.find(
            (engagementDocument) =>
              engagementDocument.Type ===
              Constants.EngagementDocumentType.EngagementDescription
          );
          if (!engagementDescriptionDocument) {
            return saveEngagementDescriptionOnSetToFinal(
              globalState,
              t,
              language
            );
          }
        }
        return Promise.resolve();
      })
      .catch(() => {
        console.error("Exception on fetch final documents");
      })
      .finally(() => {
        if (handleLoading) {
          updateApplicationLoadingState(false);
        }
      });
  }

  function fetchAndPopulateEngagementLetterSignees(): void {
    updateApplicationLoadingState(true);
    if (!accessToken) {
      handleLoginRedirect();
      return;
    }
    fetchEngagementLetterSignnes(
      globalState.currentConfiguration.digitalSigningDetails.CaseId,
      globalState.currentConfiguration.digitalSigningDetails.DocumentId,
      accessToken
    )
      .then(
        (
          engagementLetterSignees: Array<CoreInterfaces.EngagementLetterSignee>
        ) => {
          setEngagementLetterSignees(engagementLetterSignees);
          return Promise.resolve();
        }
      )
      .catch(() => {
        console.error("Exception on fetch engagement letter signees");
      })
      .finally(() => {
        updateApplicationLoadingState(false);
      });
  }

  const handleLanguageChange = (
    event: React.MouseEvent<HTMLElement>,
    language: Constants.Languages
  ) => {
    if (language !== null) {
      setLanguage(language);
      localStorage.setItem(Constants.i18nDocumentStorageKey, language);
    }
  };

  function handleLoginRedirect() {
    instance
      .loginRedirect({
        ...loginRequest,
        redirectUri: `${window.location.origin}/${Constants.Routes.GenerateDocuments}`,
      })
      .catch((error) => console.log(error));
  }

  function processDocument(
    url: string,
    payload: CoreInterfaces.EngagementManuallySigningPack,
    successMsg: string,
    errorMsg: string
  ): void {
    updateApplicationLoadingState(true);
    fetchData<CoreInterfaces.EngagementConfigurationReadPack>(
      url,
      Constants.APIMethod.POST,
      payload
    )
      .then((serverReturn: CoreInterfaces.EngagementConfigurationReadPack) => {
        updateStateOnEngagementConfigurationRead(
          serverReturn,
          dispatch,
          (service: DTOs.ServiceDTO) =>
            applyUpdatesOnQuestionChange(globalState, service)
        );
        showSnackBar(t(successMsg), "success");
      })
      .catch(() => {
        showSnackBar(t(errorMsg), "error");
      })
      .finally(() => {
        updateApplicationLoadingState(false);
      });
  }

  async function handleAddAdditionalAppendix(language: string): Promise<void> {
    const fileName =
      Constants.AdditionalAppendix[
        language as keyof typeof Constants.AdditionalAppendix
      ];
    const filePath = `./resources/${fileName}`;

    const fileContent = await new Promise<string>((resolve, reject) => {
      loadFile(filePath, (error: Error | null, content: string) => {
        if (error) {
          reject("Cannot read template file");
        } else {
          resolve(content);
        }
      });
    });
    const file = new File([fileContent], fileName, { type: "application/pdf" });

    handleFileUpload(
      businessOpportunityId,
      globalState.currentConfiguration.id,
      file,
      Constants.EngagementDocumentType.AdditionalDocument,
      Constants.MEGABYTES_IN_BYTES_25,
      [Constants.FileExtension.Pdf]
    ).then(() => {
      fetchAndPopulateEngagementDocuments(true);
    });
  }

  function startCreateFinalDocumentFlow(): void {
    updateApplicationLoadingState(true);
    setTimeout(() =>
      saveCurrentConfiguration(
        globalState,
        dispatch,
        businessOpportunityId,
        businessUnit,
        clientId
      )
        .catch((Ex: any) => {
          showSnackBar(t("General.ConfigurationSaveFail"), "error");
          console.log("Error while saving the configuration", Ex);
        })
        .then(() => {
          setLocalValuesAndRedirectToSharepoint();
        })
    );
  }

  function setLocalValuesAndRedirectToSharepoint() {
    localStorage.setItem(
      Constants.LOCAL_STORAGE_FORCE_REDIRECT_KEY,
      Constants.Routes.GenerateDocuments
    );
    localStorage.setItem(
      Constants.LOCAL_STORAGE_DOCUMENT_FLOW_KEY,
      Constants.GenerateDocumentsDocumentFlow.SetFinalVersion
    );
    instance
      .loginRedirect({
        ...sharepointAccessRequestConfig,
        redirectUri: `${window.location.origin}/${Constants.Routes.GenerateDocuments}`,
      })
      .catch((error) => console.log(error));
  }

  function createFinalDocument(accessToken: string): void {
    updateApplicationLoadingState(true);

    fetchEngagementDocuments(globalState.currentConfiguration.id)
      .then((latestDocuments) => {
        const additionalAppendixDocuments = latestDocuments.filter(
          (document) =>
            document.Type ===
            Constants.EngagementDocumentType.AdditionalDocument
        );

        const deletePromises = [];
        if (additionalAppendixDocuments.length > 0) {
          for (const doc of additionalAppendixDocuments) {
            const isTargetedAppendix = Object.values(
              Constants.AdditionalAppendix
            ).some(
              (filename) =>
                doc.OriginalFileName === filename ||
                doc.OriginalFileName.endsWith(filename)
            );

            if (isTargetedAppendix) {
              deletePromises.push(
                deleteDocument(
                  businessOpportunityId,
                  globalState.currentConfiguration.id,
                  doc
                )
              );
            }
          }
        }
        return Promise.all(deletePromises);
      })
      .then(() => handleConversionOnFinalVersionDocuments(accessToken))
      .then(() => {
        if (
          documentPageConfig.isEngagementCounselingSelected ||
          documentPageConfig.isAuditBusinessUnit ||
          documentPageConfig.isTaxBusinessUnit
        ) {
          // skip engagement description processing when Service 10 is selected
          return Promise.resolve();
        } else {
          return saveEngagementDescriptionOnSetToFinal(
            globalState,
            t,
            language
          );
        }
      })
      .then(() => {
        const apiUrl =
          globalState.remoteData.businessOpportunityInfo?.Status ===
          Constants.BusinessOpportunityStatuses.OPEN
            ? Constants.APIPath.FinalVersion
            : Constants.APIPath.FinalVersionForWonOportunities;

        let totalEngagementCost = getEngagementTotals(globalState);
        if (totalEngagementCost === 0) {
          totalEngagementCost = Number(
            environment.REACT_APP_MINIMAL_TOTAL_ENGAGEMENT_COST
          );
        }

        return handleConfigurationPersistence(
          apiUrl,
          Constants.APIMethod.POST,
          globalState,
          dispatch,
          businessOpportunityId,
          businessUnit,
          clientId,
          description,
          globalState.currentConfiguration.version,
          globalState.currentConfiguration.engagementLetterMetadata,
          globalState.currentConfiguration.digitalSigningDetails,
          totalEngagementCost,
          null,
          language
        );
      })
      .then(() =>
        deleteSPEngagementFolder(
          globalState.currentConfiguration.id,
          accessToken
        )
      )
      .then(() => {
        if (documentPageConfig.isAdditionalAppendixIncluded) {
          return handleAddAdditionalAppendix(language.toLowerCase());
        }
        return Promise.resolve();
      })
      .then(() => {
        setIsVisibleSetFinalDocumentDialog(false);
        updateApplicationLoadingState(false);
        showSnackBar(t("General.CreateFinalDocumentSuccess"), "success");
        fetchAndPopulateEngagementDocuments();
      })
      .catch(() => {
        updateApplicationLoadingState(false);
        showSnackBar(t("General.CreateFinalDocumentFail"), "error");
      });
  }

  function manuallySignDocument(
    payload: CoreInterfaces.EngagementManuallySigningPack
  ): void {
    processDocument(
      `${Constants.APIPath.ManuallySign}`,
      payload,
      "General.ManuallySignSuccess",
      "General.ManuallySignFail"
    );
    setIsVisibleSetManuallySignedDialog(false);
  }

  function sendForDigitalSigningDocument(): Promise<void> {
    const formdata = new FormData();
    const filename = t("General.Filename", {
      companyName: globalState.generalVariables.companyName,
      orgNr: globalState.generalVariables.organisationNo,
    });
    const digitalSigningUsers = resolveDigitalSigningContacts(globalState);
    const engagementSigningDto: CoreInterfaces.EngagementSigningDto = {
      BusinessOpportunityId: businessOpportunityId,
      EngagementConfigurationId: globalState.currentConfiguration.id,
      CaseId: globalState.currentConfiguration.digitalSigningDetails?.CaseId,
      FileName: filename,
      ContactPersons: digitalSigningUsers,
    };
    formdata.append(
      "EngagementSigningDto",
      JSON.stringify(engagementSigningDto)
    );
    formdata.append("token", accessToken);
    return sendFile<CoreInterfaces.EngagementConfigurationReadPack>(
      `${Constants.APIPath.SendForDigitalSigning}`,
      Constants.APIMethod.POST,
      formdata
    )
      .then((serverReturn: CoreInterfaces.EngagementConfigurationReadPack) => {
        updateStateOnEngagementConfigurationRead(
          serverReturn,
          dispatch,
          (service: DTOs.ServiceDTO) =>
            applyUpdatesOnQuestionChange(globalState, service)
        );
        fetchAndPopulateEngagementDocuments();
        showSnackBar(t("General.SendForDigitalSigningSuccess"), "success");
      })
      .catch((error: any) => {
        error.json().then((errorJson: any) => {
          if (errorJson?.ErrorCode === "DigitalSigning.InProgress") {
            showSnackBar(t("General.DigitalSigningAlreadyInProgress"), "error");
          } else {
            showSnackBar(t("General.SendForDigitalSigningFail"), "error");
          }
          fetchAndPopulateEngagementDocuments();
        });
      })
      .finally(() => {
        localStorage.removeItem(Constants.i18nDocumentStorageKey);
        setLanguage(Constants.Languages.SE);
        setAccessToken(undefined);
        updateApplicationLoadingState(false);
      });
  }

  function changeConsent(consent: Partial<CoreInterfaces.Consent>): void {
    dispatch({
      type: Constants.AppStateActions.UpdateConsent,
      payload: {
        ...globalState.currentConfiguration.consent,
        ...consent,
      },
    });
  }

  const generateEngagementDescription = async (): Promise<void> => {
    if (
      globalState.currentConfiguration.id &&
      documentPageConfig.areAllServicesCompleted
    ) {
      return generatePdfDocument(
        globalState,
        dispatch,
        t,
        language,
        updateApplicationLoadingState
      );
    }
  };

  const getCurrentEmployee = (): CoreInterfaces.ContactPersonItem => {
    const email = globalState.authProviderUserDetails?.userDetails?.trim();
    if (!email) return null as any;

    const list = globalState.remoteData?.gtContactPersons || [];
    const match = list.find(
      (cp) => cp.email?.trim().toLowerCase() === email.toLowerCase()
    );
    if (match) return match;

    const { firstName, lastName } = deriveNamesFromEmail(email);
    return {
      firstName,
      lastName,
      email,
      office: null,
    } as CoreInterfaces.ContactPersonItem;
  };

  function saveConfiguration() {
    saveCurrentConfiguration(
      globalState,
      dispatch,
      businessOpportunityId,
      businessUnit,
      clientId
    )
      .catch((Ex: any) => {
        console.log("Error while saving the configuration", Ex);
      })
      .finally(() => updateApplicationLoadingState(false));
  }

  function handleEngagementLetterGenerationFlow() {
    if (
      globalState.currentConfiguration.engagementLetterMetadata?.createdDate
    ) {
      if (!isEngagementLetterRegenerateConfirmModalVisible) {
        setIsEngagementLetterRegenerateConfirmModalVisible(true);
        return;
      } else {
        setIsEngagementLetterRegenerateConfirmModalVisible(false);
      }
    }

    localStorage.setItem(
      Constants.LOCAL_STORAGE_FORCE_REDIRECT_KEY,
      Constants.Routes.GenerateDocuments
    );
    localStorage.setItem(
      Constants.LOCAL_STORAGE_DOCUMENT_FLOW_KEY,
      Constants.GenerateDocumentsDocumentFlow.SendFilesToSharepoint
    );
    instance
      .loginRedirect({
        ...sharepointAccessRequestConfig,
        redirectUri: `${window.location.origin}/${Constants.Routes.GenerateDocuments}`,
      })
      .catch((error) => console.log(error));
  }

  async function handleGenerateDocuments(accessToken: string) {
    const initialLanguage = i18n.language;
    try {
      updateApplicationLoadingState(true);
      localStorage.removeItem(Constants.LOCAL_STORAGE_DOCUMENT_FLOW_KEY);
      await resolveSPFolder(globalState.currentConfiguration.id, accessToken);

      const engagementLetterMetadata =
        globalState.currentConfiguration.engagementLetterMetadata;
      if (
        engagementLetterMetadata?.version > 0 ||
        (engagementLetterMetadata?.version === null &&
          !!engagementLetterMetadata.createdDate)
      ) {
        await removeOldMetadataDocuments(
          globalState.currentConfiguration,
          documentPageConfig,
          accessToken
        );
      }

      const latestDocuments = await fetchEngagementDocuments(
        globalState.currentConfiguration.id
      );

      const additionalAppendixDocuments = latestDocuments.filter(
        (document) =>
          document.Type === Constants.EngagementDocumentType.AdditionalDocument
      );

      if (additionalAppendixDocuments.length > 0) {
        for (const doc of additionalAppendixDocuments) {
          const isTargetedAppendix = Object.values(
            Constants.AdditionalAppendix
          ).some(
            (filename) =>
              doc.OriginalFileName === filename ||
              doc.OriginalFileName.endsWith(filename)
          );

          if (isTargetedAppendix) {
            await deleteDocument(
              businessOpportunityId,
              globalState.currentConfiguration.id,
              doc
            );
          }
        }
      }

      const templateData: Record<Constants.EngagementDocumentType, any> =
        {} as Record<Constants.EngagementDocumentType, any>;

      await i18n.changeLanguage(language.toLocaleLowerCase());

      for (const documentType of documentPageConfig.engagementDocumentTypes) {
        templateData[documentType] = documentPageConfig.templateDataFnsMap[
          documentType
        ](globalState, genericService, language);
      }

      const documentsGenerationDate = new Date();
      const spPromises = documentPageConfig.engagementDocumentTypes.map(
        (documentTypeInput) =>
          generateEngagementDocument(
            documentTypeInput,
            templateData[documentTypeInput],
            language
          ).then((documentResponse) => {
            const documentContent = documentResponse.getZip().generate({
              type: "blob",
              mimeType:
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            });
            const documentType = documentTypeInput;

            const spFileName = `${documentType}_${formatDate(
              documentsGenerationDate,
              Constants.HelpfulConstants.DateFormatTrimYearMonthDayHourMinutes
            )}.${Constants.FileExtension.Docx}`;

            return sendFileToSharepoint(
              globalState.currentConfiguration.id,
              spFileName,
              documentContent,
              accessToken
            );
          })
      );
      await Promise.all(spPromises);

      if (documentPageConfig.isAdditionalAppendixIncluded) {
        await handleAddAdditionalAppendix(language.toLowerCase());
      }

      const currentEmployee = getCurrentEmployee();
      const actionPack: {
        currentUser: CoreInterfaces.Employee;
        generationDate: Date;
        version: number;
      } = {
        currentUser: {
          FirstName: currentEmployee.firstName,
          LastName: currentEmployee.lastName,
          Email: currentEmployee.email,
          Office: currentEmployee.office,
          Id: null,
        },
        generationDate: documentsGenerationDate,
        version:
          globalState.currentConfiguration.engagementLetterMetadata?.version ??
          1,
      };
      dispatch({
        type: Constants.AppStateActions.UpdateEngagementDocumentsMetadata,
        payload: actionPack,
      });
      setTimeout(() => saveConfiguration());
    } catch (exception: any) {
      console.error("Engagement document generation failed:", exception);
      let errorMessage = t("General.EngagementLetterGenerateError");
      if (exception.status === Constants.HttpStatusCodes.Locked) {
        errorMessage = t("General.DocumentOpenedByOtherClient");
      }
      showSnackBar(
        errorMessage,
        "error",
        Constants.ERROR_MESSAGE_VISIBILITY_MILLIS
      );
      updateApplicationLoadingState(false);
    } finally {
      i18n.changeLanguage(initialLanguage);
    }
  }

  function handleConversionOnFinalVersionDocuments(
    accessToken: string
  ): Promise<Array<void>> {
    localStorage.removeItem(Constants.LOCAL_STORAGE_DOCUMENT_FLOW_KEY);
    const fileHandlePromises = documentPageConfig.engagementDocumentTypes.map(
      (documentType) =>
        executeFinalizeEngagementDocumentFlow(
          businessOpportunityId,
          globalState,
          documentType,
          accessToken
        )
    );

    return Promise.all(fileHandlePromises);
  }

  function handleDocumentAction(linkData: any, readMode: boolean): void {
    if (!documentPageConfig.isFinalVersion) {
      openSharepointURLInMsWordClient(
        globalState.currentConfiguration.id,
        linkData.DownloadableFileName,
        readMode,
        globalState.currentConfiguration.engagementLetterMetadata
      );
    }
  }

  function handleOpenFinalDocumentDialogClick(): void {
    setIsVisibleSetFinalDocumentDialog(true);
    globalState.currentConfiguration.totalCostManualOverride = null;
  }

  let engagementLetterAndAppendixDocuments: Array<{
    Id: string;
    OriginalFileName: string;
    DownloadableFileName: string;
  }> = [];

  if (!documentPageConfig.isFinalVersion) {
    if (!!globalState.currentConfiguration.engagementLetterMetadata) {
      engagementLetterAndAppendixDocuments =
        documentPageConfig.engagementDocumentTypes.map((documentType) => {
          return {
            Id: `engagementDocument${documentType}`,
            OriginalFileName: t(`General.${documentType}`),
            DownloadableFileName: documentType,
          };
        });
    }
  } else {
    const engagementDocumentTypes: Constants.EngagementDocumentType[] =
      documentPageConfig.engagementDocumentTypes;

    engagementLetterAndAppendixDocuments = engagementDocuments
      .filter((engagementDocument) => {
        return engagementDocumentTypes
          .map((documentType) => documentType)
          .includes(engagementDocument.Type);
      })
      .map((document) => ({
        ...document,
        OriginalFileName: t(`General.${document.Type}`),
        DownloadableFileName: document.Type,
      }));
  }

  function convertDocToPDF(doc: any): Promise<Blob> {
    const formdata = new FormData();
    const out = doc.getZip().generate({
      type: "blob",
      mimeType:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
    formdata.append("file", out);
    return fetchPdfFile(
      Constants.APIPath.ConvertToPdf,
      Constants.APIMethod.POST,
      formdata
    );
  }

  function saveEngagementDescriptionOnSetToFinal(
    globalState: CoreInterfaces.AppState,
    t: Function,
    language: string
  ): Promise<void> {
    if (
      businessUnit !== Constants.BusinessUnit.Audit &&
      businessUnit !== Constants.BusinessUnit.Tax
    ) {
      return generateEngagementDescriptionWordDocument(globalState, t, language)
        .then((doc) => convertDocToPDF(doc))
        .then((pdfFileContent: Blob) => {
          const documentType =
            Constants.EngagementDocumentType.EngagementDescription;
          return handleFileUpload(
            businessOpportunityId,
            globalState.currentConfiguration.id,
            pdfFileContent,
            documentType,
            null,
            [],
            `${documentType}.${Constants.FileExtension.Pdf}`
          );
        });
    }
  }

  function sendForDigitalSigning(): void {
    localStorage.setItem(
      Constants.LOCAL_STORAGE_SEND_FOR_DIGITAL_SIGNING_FLOW_KEY,
      Constants.GenerateDocumentsDocumentFlow.SendForDigitalSigning
    );
    handleLoginRedirect();
  }

  function cancelSigningProcess(comment: string): Promise<void> {
    updateApplicationLoadingState(true);
    return doCancelSigningProcess(
      businessOpportunityId,
      globalState.currentConfiguration.id,
      comment,
      accessToken
    )
      .then((serverReturn: CoreInterfaces.EngagementConfigurationReadPack) => {
        updateStateOnEngagementConfigurationRead(
          serverReturn,
          dispatch,
          (service: DTOs.ServiceDTO) =>
            applyUpdatesOnQuestionChange(globalState, service)
        );
        fetchAndPopulateEngagementDocuments();
        setIsVisibleCancelSigningDialog(false);
        showSnackBar(t("General.CancelDigitalSigningSuccess"), "success");
      })
      .catch(() => {
        fetchAndPopulateEngagementDocuments();
        showSnackBar(t("General.CancelDigitalSigningFail"), "error");
      })
      .finally(() => {
        updateApplicationLoadingState(false);
      });
  }

  function changeEmail(newEmail: string): void {
    updateApplicationLoadingState(true);

    const payload = {
      BusinessOpportunityId: businessOpportunityId,
      EngagementConfigurationId: globalState.currentConfiguration.id,
      RecipientId: selectedEngagementLetterSignee.Id,
      Email: newEmail,
    };
    const customHeaders = [
      {
        key: Constants.DigitalSigningAuthTokenRequestHeaderKey,
        value: accessToken,
      },
    ];
    fetchData(
      Constants.APIPath.DigitalSigningEditEmail,
      Constants.APIMethod.PUT,
      payload,
      {},
      false,
      customHeaders
    )
      .then(() => {
        fetchAndPopulateEngagementLetterSignees();
        setIsVisibleChangeEmailDialog(false);
      })
      .catch((error) => {
        console.error("Failed to change email:", error);
        showSnackBar(t("General.AnErrorHasOccured"), "error");
      })
      .finally(() => {
        updateApplicationLoadingState(false);
      });
  }

  function sendReminder(): void {
    updateApplicationLoadingState(true);
    const payload = {
      BusinessOpportunityId: businessOpportunityId,
      EngagementConfigurationId: globalState.currentConfiguration.id,
      RecipientId: selectedEngagementLetterSignee.Id,
    };
    const customHeaders = [
      {
        key: Constants.DigitalSigningAuthTokenRequestHeaderKey,
        value: accessToken,
      },
    ];
    fetchData(
      Constants.APIPath.DigitalSigningSendReminder,
      Constants.APIMethod.POST,
      payload,
      {},
      false,
      customHeaders
    )
      .then(() => {
        fetchAndPopulateEngagementLetterSignees();
        setIsVisibleNotifyDialog(false);
      })
      .catch(() => {
        showSnackBar(t("General.AnErrorHasOccured"), "error");
      })
      .finally(() => {
        updateApplicationLoadingState(false);
      });
  }

  function copyLink(
    engagementLetterSignee: CoreInterfaces.EngagementLetterSignee
  ): void {
    const payload = {
      BusinessOpportunityId: businessOpportunityId,
      EngagementConfigurationId: globalState.currentConfiguration.id,
      RecipientId: engagementLetterSignee.Id,
    };
    const customHeaders = [
      {
        key: Constants.DigitalSigningAuthTokenRequestHeaderKey,
        value: accessToken,
      },
    ];
    updateApplicationLoadingState(true);
    fetchData<{ SigningLink: string }>(
      Constants.APIPath.DigitalSigningRecipientsSigningLink,
      Constants.APIMethod.POST,
      payload,
      {},
      false,
      customHeaders
    )
      .then((serverReturn: { SigningLink: string }) => {
        navigator.clipboard.writeText(serverReturn?.SigningLink);
        const extractedId = extractSigningIdFromUrl(serverReturn?.SigningLink);
        if (extractedId) {
          setEnvelopeIds((prev) => ({
            ...prev,
            [engagementLetterSignee.Id]: extractedId,
          }));
        }

        showSnackBar(t("General.LinkCopiedToClipboard"), "success");
      })
      .catch(() => {
        showSnackBar(t("General.AnErrorHasOccured"), "error");
      })
      .finally(() => {
        updateApplicationLoadingState(false);
      });
  }

  function downloadSignedDocument(): void {
    updateApplicationLoadingState(true);
    fetchDownloadSignedDocument(
      businessOpportunityId,
      globalState.currentConfiguration.id,
      accessToken
    )
      .catch(() => {
        showSnackBar(t("General.AnErrorHasOccured"), "error");
      })
      .finally(() => {
        updateApplicationLoadingState(false);
      });
  }

  function signingStatusChange(): void {
    if (!accessToken) {
      handleLoginRedirect();
      return;
    }
    fetchSigningStatus(
      businessOpportunityId,
      globalState.currentConfiguration.id,
      accessToken
    )
      .then((status) => {
        if (status) {
          fetchEngagementConfigurationData(
            businessOpportunityId,
            globalState.currentConfiguration.id
          ).then((engagementConfigurationData) => {
            updateStateOnEngagementConfigurationRead(
              engagementConfigurationData,
              dispatch,
              // eslint-disable-next-line
              () => {},
              null,
              Constants.UpdateStateOnEngagementConfigurationReadScenario
                .LoadConfiguration
            );
            updateApplicationLoadingState(false);
          });
        }
      })
      .catch(() => {
        showSnackBar(t("General.AnErrorHasOccured"), "error");
      });
  }

  return {
    // Context values
    t,
    globalState,
    businessUnit,
    // State
    language,
    engagementDocuments,
    engagementLetterSignees,
    envelopeIds,
    pageState,
    selectedEngagementLetterSignee,
    // Modal visibility state + setters
    isEngagementLetterRegenerateConfirmModalVisible,
    setIsEngagementLetterRegenerateConfirmModalVisible,
    isVisibleSetFinalDocumentDialog,
    setIsVisibleSetFinalDocumentDialog,
    isVisibleSetManuallySignedDialog,
    setIsVisibleSetManuallySignedDialog,
    isVisibleCancelSigningDialog,
    setIsVisibleCancelSigningDialog,
    isVisibleChangeEmailDialog,
    setIsVisibleChangeEmailDialog,
    isVisibleNotifyDialog,
    setIsVisibleNotifyDialog,
    setSelectedEngagementLetterSignee,
    // Derived values
    documentPageConfig,
    configurationName,
    createdBy,
    description,
    engagementLetterAndAppendixDocuments,
    engagementDescriptionDocumentArray,
    isEmailMissingDigitalSigningUsers,
    isEmailContainingForbiddenCharactersDigitalSigningUsers,
    // Handlers
    handleLanguageChange,
    handleEngagementLetterGenerationFlow,
    handleDocumentAction,
    fetchAndPopulateEngagementDocuments,
    handleOpenFinalDocumentDialogClick,
    startCreateFinalDocumentFlow,
    manuallySignDocument,
    cancelSigningProcess,
    changeEmail,
    sendReminder,
    copyLink,
    downloadSignedDocument,
    sendForDigitalSigning,
    changeConsent,
    generateEngagementDescription,
    isDigitallySigned: digitallySigned,
    isObsolete: signingObsolete,
    isDigitallySigningEnabled: isDigitallySigningChecked(globalState),
  };
}
